import { gql, useQuery } from '@apollo/client';
import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import IconVariant from '@apollo/icons/default/IconVariant.svg';
import {
  Button,
  Card,
  CardBody,
  Heading,
  IconButton,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { Observatory } from '@apollo/space-kit/illustrations/Observatory';
import classnames from 'classnames';
import { clamp, range, uniq } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  Link as ReactRouterLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { graphRefToString } from '../../hooks/useGraphRef';
import { ConflictBadgeWithLink } from '../../proposalLayout/ConflictBadge';
import { ProposalStatusBadge } from '../../proposalLayout/ProposalStatusBadge';
import {
  graphProposalListRouteConfig,
  graphRouteConfig,
  proposalEditorRouteConfig,
  proposalRouteConfig,
  variantProposalListRouteConfig,
} from '../../routes';
import { ProposeChangesModal } from '../../shared/proposeChangesModal/ProposeChangesModal';

import { ProposalsFilterToggles } from './ProposalsFilterToggles';
import { ProposeChangesButton } from './ProposeChangesButton';

const PROPOSAL_LIST_PAGE_SIZE = 25;
const MIN_TOTAL_WITH_ALL_PAGE_NUMBERS_VISIBLE = 10;
const PAGE_BUFFER = 2;
const MAX_VISIBLE_SUBGRAPHS = 5;

export const ProposalList = ({
  allSubgraphsByVariant,
  setProposalDropdownButton,
  proposalDropdownButton,
  proposalDropdownButtonRefs,
}: {
  allSubgraphsByVariant: Record<string, string[]>;
  proposalDropdownButton?: 'header' | 'empty';
  setProposalDropdownButton?: React.Dispatch<
    React.SetStateAction<'header' | 'empty'>
  >;
  proposalDropdownButtonRefs?: React.MutableRefObject<{
    [name: string]: React.RefObject<HTMLButtonElement>;
  }>;
}) => {
  const {
    graphId,
    graphVariant,
    page = 1,
    filterBySubgraphs,
    filterByVariants,
    filterByStatuses,
    view = 'graph',
  } = useRouteParams(
    graphProposalListRouteConfig,
    variantProposalListRouteConfig,
  );
  const isGraphLevelProposalList = !!useRouteMatch(
    graphProposalListRouteConfig.definition,
  );

  const location = useLocation();

  const { data, loading } = useQuery<
    GraphQLTypes.ProposalListQuery,
    GraphQLTypes.ProposalListQueryVariables
  >(
    gql`
      query ProposalListQuery(
        $graphId: ID!
        $limit: Int
        $offset: Int
        $filterBy: ProposalsFilterInput
      ) {
        graph(id: $graphId) {
          id
          name
          roles {
            canCreateProposal
          }
          proposalDescriptionTemplate
          hasProposals: proposals(limit: 0) {
            totalCount
          }
          proposals(limit: $limit, offset: $offset, filterBy: $filterBy) {
            totalCount
            proposals {
              id
              mergeBaseCompositionId
              canEditProposal
              displayName
              status
              createdAt
              createdBy {
                id
                ... on User {
                  fullName
                  avatarUrl
                }
              }
              sourceVariant {
                id
                name
              }
              latestRevision {
                id
                mergeBaseLaunch {
                  id
                  buildInput {
                    ... on CompositionBuildInput {
                      subgraphs {
                        hash
                        name
                      }
                    }
                  }
                }
                launch {
                  id
                  buildInput {
                    ... on CompositionBuildInput {
                      subgraphs {
                        hash
                        name
                      }
                    }
                  }
                }
                previousRevision {
                  id
                  launch {
                    id
                    buildInput {
                      ... on CompositionBuildInput {
                        subgraphs {
                          name
                          hash
                        }
                      }
                    }
                  }
                }
              }
              backingVariant {
                id
                name
                latestLaunch {
                  id
                  buildInput {
                    ... on CompositionBuildInput {
                      subgraphs {
                        name
                        hash
                      }
                    }
                  }
                }
                firstLaunch: launchHistory(limit: 1, orderBy: CREATED_ASC) {
                  id
                  createdAt
                  buildInput {
                    ... on CompositionBuildInput {
                      subgraphs {
                        hash
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        graphId,
        offset: PROPOSAL_LIST_PAGE_SIZE * (page - 1),
        limit: PROPOSAL_LIST_PAGE_SIZE,
        filterBy: {
          sourceVariants:
            view === 'variant' && graphVariant
              ? [graphVariant]
              : (filterByVariants ?? null),
          subgraphs: isMinLengthArray(1, filterBySubgraphs)
            ? filterBySubgraphs
            : null,
          status: isMinLengthArray(1, filterByStatuses)
            ? filterByStatuses
            : null,
        },
      },
      // ? NOTE: When we paginate between pages, the data may change so we should
      // ? always use the data from the network request.
      fetchPolicy: 'network-only',
    },
  );

  const totalCount = data?.graph?.proposals.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PROPOSAL_LIST_PAGE_SIZE) || 1;

  const visiblePages = useMemo(() => {
    if (totalPages <= MIN_TOTAL_WITH_ALL_PAGE_NUMBERS_VISIBLE) {
      return range(1, totalPages + 1);
    }

    // ? Note: These two clamps get the start and end numbers for the range we
    // ? should display as visitable pages in our pagination list. To do this we:
    // ?  1. use a buffer to get the initial padded space
    // ?  2. add extra padding if we are in a spot where our start or end is
    // ?     close to the 1 or the total page count (i.e. 1 ... 3 4 (5) 6 7 ... 32
    // ?     vs 1 2 3 4 (5) 6 7 ... 32). In our case it is when the page
    // ?     is buffer + 2 (+ 1 for start because we are 1 indexed)
    // ?  3. clamp the start/end value so it is explicitly between 1 and total
    // ?     page count and then make sure we add extra padding if the current
    // ?     page is at the start or the end of the list
    // ?     (i.e. (1) 2 3 ... 32 vs (1) 2 3 4 5 ... 32 ). This value should be
    // ?     our the start or end +/- the buffer + 2.
    const startPage = clamp(
      page - PAGE_BUFFER - (page === 1 + PAGE_BUFFER + 2 ? 1 : 0),
      2,
      totalPages - PAGE_BUFFER + 2,
    );
    const endPage = clamp(
      page + PAGE_BUFFER + (page === totalPages - PAGE_BUFFER + 2 ? 1 : 0),
      1 + PAGE_BUFFER + 2,
      totalPages - 1,
    );

    return [1, ...range(startPage, endPage + 1), totalPages];
  }, [page, totalPages]);

  const allSubgraphs = useMemo(() => {
    return uniq(Object.values(allSubgraphsByVariant).flat());
  }, [allSubgraphsByVariant]);

  const proposals = data?.graph?.proposals.proposals;

  const hasActiveFilters =
    !!filterByStatuses ||
    (filterBySubgraphs && isMinLengthArray(1, filterBySubgraphs)) ||
    (filterByVariants && isMinLengthArray(1, filterByVariants));
  const hasProposals = (data?.graph?.hasProposals.totalCount ?? 0) > 0;

  return (
    <>
      <div className="mx-8 mt-8 flex items-end justify-between">
        {view === 'graph' ? (
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold">
              All proposals for {data?.graph?.name ?? graphId}{' '}
              {data?.graph?.proposals &&
                `(${data?.graph?.proposals.totalCount})`}
            </h2>
            <p className="text-base text-secondary">
              A space to collaborate on upcoming changes to your graph.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold">
              Proposals targeting {graphVariant}{' '}
              {data?.graph?.proposals &&
                `(${data?.graph?.proposals.totalCount})`}
            </h2>
            <p className="text-base text-secondary">
              A list of schema proposals that use{' '}
              {graphRefToString({
                graphId,
                graphVariant: graphVariant ?? '',
              })}{' '}
              as their source variant.
            </p>
          </div>
        )}

        <ProposalsFilterToggles
          allSubgraphs={allSubgraphs}
          allVariants={Object.keys(allSubgraphsByVariant)}
          className="flex gap-2 max-md:flex-wrap"
        />
      </div>

      <Card
        className={classnames(
          'm-8 shrink-0',
          !isMinLengthArray(1, proposals) && 'flex-1',
        )}
      >
        <CardBody>
          {loading ? (
            <Loading />
          ) : !isMinLengthArray(1, proposals) ? (
            hasActiveFilters && hasProposals ? (
              <div className="flex size-full flex-col items-center justify-center">
                <Observatory className="mb-3" />
                <Heading size="2xl" className="mb-2 text-center">
                  No matches found
                </Heading>
                <p className="mb-3 max-w-[65ch] text-center text-base text-secondary">
                  Try removing some filters or using different criteria.
                </p>
                <Button
                  variant="primary"
                  as={ReactRouterLink}
                  to={
                    isGraphLevelProposalList
                      ? graphProposalListRouteConfig.locationFrom({
                          location,
                          patch: {
                            filterByStatuses: RouteConfig.DELETE,
                            filterByVariants: RouteConfig.DELETE,
                            filterBySubgraphs: RouteConfig.DELETE,
                            page: RouteConfig.DELETE,
                          },
                        })
                      : variantProposalListRouteConfig.locationFrom({
                          location,
                          patch: {
                            filterByStatuses: RouteConfig.DELETE,
                            filterByVariants: RouteConfig.DELETE,
                            filterBySubgraphs: RouteConfig.DELETE,
                            page: RouteConfig.DELETE,
                          },
                        })
                  }
                >
                  Reset filters
                </Button>
              </div>
            ) : (
              <div className="flex size-full flex-col items-center justify-center">
                <Observatory className="mb-3" />
                <Heading size="2xl" className="mb-3 text-center">
                  Safely suggest changes to your schema
                </Heading>
                <p className="max-w-[65ch] text-center text-base text-secondary">
                  Schema proposals give teams a collaborative space to evolve
                  their graphs. Edit subgraph schemas, start discussions, and
                  request reviews from your teammates.
                </p>
                <ProposeChangesButton
                  // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                  // TODO(Maya) add link to synthetics test
                  data-testid="proposal_list_empty_state_propose_changes_button"
                  ref={(el: HTMLButtonElement | null) => {
                    if (proposalDropdownButtonRefs) {
                      // eslint-disable-next-line no-param-reassign
                      proposalDropdownButtonRefs.current.empty = {
                        current: el,
                      };
                    }
                  }}
                  disabled={!data?.graph?.roles?.canCreateProposal}
                  className="mt-4"
                  onClickMenuButton={() => setProposalDropdownButton?.('empty')}
                />
              </div>
            )
          ) : (
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Proposal details</Th>
                    <Th className="w-28">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {proposals.map((proposal) => (
                    <ProposalListTableRow
                      key={proposal.id}
                      proposal={proposal}
                      isGraphLevelProposalList={isGraphLevelProposalList}
                    />
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>
      {!loading && totalCount > 0 && (
        <nav
          aria-label="pagination"
          className="mx-auto mb-6 flex items-center space-x-3"
        >
          <IconButton
            variant="hidden"
            icon={<IconChevronLeft />}
            aria-label="Previous page"
            isDisabled={page === 1}
            as={ReactRouterLink}
            to={
              isGraphLevelProposalList
                ? graphProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      page: page - 1,
                    },
                  })
                : variantProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      page: page - 1,
                    },
                  })
            }
          />
          {visiblePages.map((value, i) => {
            const shouldShowEllipsis =
              i > 0 && value - (visiblePages[i - 1] ?? 0) > 1;
            return (
              <React.Fragment key={value}>
                {shouldShowEllipsis && (
                  <div
                    className="cursor-not-allowed px-2 text-sm font-semibold text-secondary"
                    role="presentation"
                  >
                    ...
                  </div>
                )}
                <Button
                  variant={page === value ? 'primary' : 'hidden'}
                  as={ReactRouterLink}
                  to={
                    isGraphLevelProposalList
                      ? graphProposalListRouteConfig.locationFrom({
                          location,
                          patch: {
                            page: value,
                          },
                        })
                      : variantProposalListRouteConfig.locationFrom({
                          location,
                          patch: {
                            page: value,
                          },
                        })
                  }
                  {...(page === value ? { 'aria-current': 'page' } : {})}
                >
                  {value}
                </Button>
              </React.Fragment>
            );
          })}
          <IconButton
            variant="hidden"
            icon={<IconChevronRight />}
            aria-label="Next page"
            isDisabled={page === totalPages}
            as={ReactRouterLink}
            to={
              isGraphLevelProposalList
                ? graphProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      page: page + 1,
                    },
                  })
                : variantProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      page: page + 1,
                    },
                  })
            }
          />
        </nav>
      )}
      <LazyUrlModal name={Config.modals.proposeSchemaChanges}>
        {({ closeModal, isOpen }) => (
          <ProposeChangesModal
            isOpen={isOpen}
            onClose={() =>
              closeModal(undefined, ['shouldPromptForNewSubgraphName'])
            }
            finalFocusRef={
              proposalDropdownButtonRefs?.current[
                proposalDropdownButton ?? 'header'
              ]
            }
            existingSubgraphNames={allSubgraphsByVariant}
            proposalDescriptionTemplate={
              data?.graph?.proposalDescriptionTemplate ?? undefined
            }
          />
        )}
      </LazyUrlModal>
    </>
  );
};

const ProposalListTableRow = ({
  proposal,
  isGraphLevelProposalList,
}: {
  proposal: NonNullable<
    GraphQLTypes.ProposalListQuery['graph']
  >['proposals']['proposals'][number];
  isGraphLevelProposalList: boolean;
}) => {
  const [inViewRef, inView] = useInView();
  const [shouldShowAllSubgraphs, setShouldShowAllSubgraphs] = useState(false);
  const location = useLocation();
  const shouldShowConflicts = useLDFlag('nebula_proposals_merge_upstream');
  const variant = proposal.backingVariant;
  const selectedRevision =
    proposal.latestRevision?.__typename === 'ProposalRevision'
      ? proposal.latestRevision
      : undefined;

  // Note: Rebase conflicts are costly to fetch because they require pulling in the SDL
  // to perform  calculations. We should only fetch them when we know we will be
  // able to show them as well. In the future we plan on adding dataloaders around this
  // and may also precalculate them in the future but this should help in the meantime.
  const { data } = useQuery<
    GraphQLTypes.ProposalRebaseConflictsQuery,
    GraphQLTypes.ProposalRebaseConflictsQueryVariables
  >(
    gql`
      query ProposalRebaseConflictsQuery($proposalId: ID!) {
        proposal(id: $proposalId) {
          id
          rebaseConflicts {
            ... on RebaseConflictData {
              count
            }
          }
        }
      }
    `,
    {
      variables: {
        proposalId: proposal.id,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      skip:
        !inView ||
        !shouldShowConflicts ||
        // If a proposal doesn't have a mergeBaseCompositionId, the proposal wont have any conflicts
        !proposal.mergeBaseCompositionId ||
        // Implemented and closed proposals don't have conflicts
        proposal.status === ProposalStatus.IMPLEMENTED ||
        proposal.status === ProposalStatus.CLOSED,
    },
  );

  const hasConflictsWithSourceVariant =
    data?.proposal?.rebaseConflicts?.__typename === 'RebaseConflictData' &&
    data?.proposal?.rebaseConflicts.count > 0 &&
    proposal.mergeBaseCompositionId;

  const shouldShowConflictBadge =
    shouldShowConflicts && hasConflictsWithSourceVariant;

  const subgraphInfoMap = useMemo(() => {
    const lookup: Record<
      string,
      { old?: string; new: string } | { old: string; new?: string }
    > = {};
    const buildInput =
      variant?.latestLaunch?.buildInput?.__typename === 'CompositionBuildInput'
        ? variant.latestLaunch.buildInput
        : undefined;

    // get mergeBase build input if we are looking at a revision
    const mergeBaseLaunch = selectedRevision?.mergeBaseLaunch;

    const currentRevisionOrLatestBuildInput = selectedRevision
      ? selectedRevision?.launch?.buildInput?.__typename ===
        'CompositionBuildInput'
        ? selectedRevision.launch.buildInput
        : undefined
      : buildInput;
    const firstBuildInput = variant?.firstLaunch?.[0]?.buildInput;
    const previousRevisionBuildInput =
      selectedRevision?.previousRevision?.launch?.buildInput;

    if (isMinLengthArray(1, currentRevisionOrLatestBuildInput?.subgraphs)) {
      currentRevisionOrLatestBuildInput?.subgraphs.forEach((subgraphConfig) => {
        lookup[subgraphConfig.name] = {
          new: subgraphConfig.hash,
        };
      });

      if (mergeBaseLaunch?.buildInput?.__typename === 'CompositionBuildInput') {
        mergeBaseLaunch?.buildInput?.subgraphs.forEach((subgraphConfig) => {
          getOrSetRecord(
            subgraphConfig.name,
            { old: subgraphConfig.hash },
            lookup,
          ).old = subgraphConfig.hash;
        });
      } else if (
        previousRevisionBuildInput?.__typename === 'CompositionBuildInput'
      ) {
        previousRevisionBuildInput.subgraphs.forEach((subgraphConfig) => {
          getOrSetRecord(
            subgraphConfig.name,
            { old: subgraphConfig.hash },
            lookup,
          ).old = subgraphConfig.hash;
        });
      } else if (firstBuildInput?.__typename === 'CompositionBuildInput') {
        firstBuildInput.subgraphs.forEach((subgraphConfig) => {
          getOrSetRecord(
            subgraphConfig.name,
            { old: subgraphConfig.hash },
            lookup,
          ).old = subgraphConfig.hash;
        });
      } else {
        throw new Error(`unknown type ${firstBuildInput?.__typename}`);
      }
    }

    return lookup;
  }, [
    variant?.latestLaunch?.buildInput,
    variant?.firstLaunch,
    selectedRevision,
  ]);
  const subgraphNames = useMemo(
    () =>
      Object.entries(subgraphInfoMap)
        .filter(([, hashes]) => hashes.old !== hashes.new)
        .map(([name]) => name)
        .sort((s1, s2) => s1.localeCompare(s2)),
    [subgraphInfoMap],
  );

  return (
    <Tr ref={inViewRef}>
      <Td>
        <div className="flex flex-col space-y-2">
          <Link
            to={proposalRouteConfig.locationFrom({
              location,
              patch: {
                graphVariant: proposal.backingVariant.name,
              },
              fromRouteConfig: graphRouteConfig,
            })}
            {...(!isGraphLevelProposalList
              ? {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
              : {})}
          >
            {proposal?.displayName}
          </Link>
          {proposal && (
            <>
              <div className="flex items-start">
                <IconTime className="mr-2 mt-1 text-icon-primary" />
                <span className="text-base text-secondary">
                  Created <RelativeTimestamp value={proposal?.createdAt} /> by{' '}
                  {proposal.createdBy?.__typename === 'User'
                    ? proposal.createdBy.fullName
                    : 'Unknown'}
                </span>
              </div>
              <div className="flex items-start">
                <IconVariant className="mr-2 mt-1 text-icon-primary" />
                <span className="text-base text-secondary">
                  Started from {proposal.sourceVariant.name}
                </span>
              </div>
            </>
          )}
          {subgraphNames.length > 0 && isMinLengthArray(1, subgraphNames) && (
            <div className="mt-3 flex items-start">
              <IconSingleService className="mr-2 mt-1 text-icon-primary" />
              <span className="text-base text-secondary">
                Changes to{' '}
                {subgraphNames
                  .slice(
                    0,
                    shouldShowAllSubgraphs
                      ? subgraphNames.length
                      : MAX_VISIBLE_SUBGRAPHS,
                  )
                  .map((subgraph, i) => (
                    <span key={subgraph}>
                      {i > 0 && ', '}

                      <Link
                        to={proposalEditorRouteConfig.locationFrom({
                          location,
                          patch: {
                            graphVariant: proposal.backingVariant.name,
                            selectedSchema: subgraph,
                          },
                          fromRouteConfig: graphRouteConfig,
                        })}
                      >
                        {subgraph}
                      </Link>
                    </span>
                  ))}
                {isMinLengthArray(MAX_VISIBLE_SUBGRAPHS + 1, subgraphNames) &&
                  (!shouldShowAllSubgraphs ? (
                    <>
                      <span>
                        , +{subgraphNames.length - MAX_VISIBLE_SUBGRAPHS} more
                      </span>
                      <Button
                        variant="secondary"
                        onClick={() => setShouldShowAllSubgraphs(true)}
                        size="sm"
                        className="ml-2 inline-block"
                      >
                        Show all
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => setShouldShowAllSubgraphs(false)}
                      size="sm"
                      className="mt-2 block"
                    >
                      Show less
                    </Button>
                  ))}
              </span>
            </div>
          )}
        </div>
      </Td>
      <Td>
        <ProposalStatusBadge
          status={proposal.status}
          className={classnames({
            'mr-2': shouldShowConflictBadge,
          })}
        />
        {shouldShowConflictBadge && (
          <ConflictBadgeWithLink
            isDisabled={!proposal.canEditProposal}
            graphVariantName={proposal.backingVariant.name}
          />
        )}
      </Td>
    </Tr>
  );
};
